import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
}
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function italicizeFirstWord(title: string): {
  first: string;
  rest: string;
} {
  const trimmed = title.trim();
  const spaceIdx = trimmed.indexOf(" ");
  if (spaceIdx === -1) return {
    first: trimmed,
    rest: ""
  };
  return {
    first: trimmed.slice(0, spaceIdx),
    rest: trimmed.slice(spaceIdx)
  };
}
function FeaturedPostSkeleton() {
  return <div className="col-span-12 lg:col-span-6 animate-pulse">
      <div className="aspect-[4/5] w-full bg-slate-200" />
      <div className="mt-6 h-3 w-40 bg-slate-200" />
      <div className="mt-4 h-8 w-3/4 bg-slate-200" />
      <div className="mt-3 h-4 w-full bg-slate-200" />
    </div>;
}
function SmallPostSkeleton({
  index
}: {
  index: number;
}) {
  return <div data-index={index} className="grid grid-cols-12 gap-4 animate-pulse">
      <div className="col-span-12 sm:col-span-5">
        <div className="aspect-[3/2] w-full bg-slate-200" />
      </div>
      <div className="col-span-12 sm:col-span-7 flex flex-col gap-3">
        <div className="h-3 w-32 bg-slate-200" />
        <div className="h-6 w-3/4 bg-slate-200" />
        <div className="h-4 w-full bg-slate-200" />
      </div>
    </div>;
}
function JournalPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-12 gap-x-6 gap-y-12">
        <FeaturedPostSkeleton />
        <div className="col-span-12 lg:col-span-6 flex flex-col gap-12">
          <SmallPostSkeleton index={0} />
          <div className="h-px w-full bg-slate-300/40" />
          <SmallPostSkeleton index={1} />
        </div>
      </div>;
  }
  if (error) {
    return <p className="font-mono text-xs uppercase tracking-[0.18em] text-blue-900">
        Failed to load technical insights.
      </p>;
  }
  if (!posts || posts.length === 0) {
    return <p className="font-mono text-xs uppercase tracking-[0.18em] text-blue-900">
        No case studies available.
      </p>;
  }
  const featured = posts[0];
  const rest = posts.slice(1, 3);
  const featuredTitleParts = italicizeFirstWord(featured.title || "Untitled");
  const featuredCategory = featured.categories && featured.categories.length > 0 ? featured.categories[0].name : "Case Study";
  const featuredExcerpt = stripHtml(featured.excerpt);
  return <div className="grid grid-cols-12 gap-x-6 gap-y-14">
      <article data-index={0} className="col-span-12 lg:col-span-6 group">
        <Link to={featured.link || "#"} className="block">
          <div className="relative overflow-hidden border border-slate-200 rounded-lg shadow-md">
            <div className="aspect-[4/5] w-full overflow-hidden bg-slate-100">
              {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-slate-100" />}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.18em] text-blue-700">
            <span>{formatDate(featured.date)}</span>
            <span aria-hidden>—</span>
            <span>{featuredCategory}</span>
            <span className="ml-1 inline-block h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
          </div>

          <h3 className="font-default mt-4 text-[2.125rem] leading-[1.1] tracking-[-0.018em] text-slate-900">
            <span>{featuredTitleParts.first}</span>
            <em className="not-italic" style={{
            fontStyle: "italic",
            color: "#2382DA"
          }}>
              {featuredTitleParts.rest}
            </em>
          </h3>

          {featuredExcerpt && <p className="mt-3 line-clamp-2 text-base leading-[1.55] text-slate-600">
              {featuredExcerpt}
            </p>}

          <span className="mt-5 inline-flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.18em] text-blue-900">
            <span className="border-b-2 border-blue-500 pb-1 transition-all duration-200 group-hover:pb-[2px]">
              View Case Study
            </span>
            <span className="inline-block h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
          </span>
        </Link>
      </article>

      <div className="col-span-12 lg:col-span-6 flex flex-col gap-12 relative">
        <span aria-hidden className="hidden lg:block absolute -left-3 top-0 bottom-0 w-px bg-slate-300/30" />

        {rest.map((post, i) => {
        const titleParts = italicizeFirstWord(post.title || "Untitled");
        const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Case Study";
        const excerpt = stripHtml(post.excerpt);
        return <div key={post.id} data-index={i}>
              <article className="group">
                <Link to={post.link || "#"} className="grid grid-cols-12 gap-4 sm:gap-6">
                  <div className="col-span-12 sm:col-span-5">
                    <div className="relative overflow-hidden border border-slate-200 rounded-lg shadow-sm">
                      <div className="aspect-[3/2] w-full overflow-hidden bg-slate-100">
                        {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-slate-100" />}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12 sm:col-span-7 flex flex-col">
                    <div className="flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-blue-700">
                      <span>{formatDate(post.date)}</span>
                      <span aria-hidden>—</span>
                      <span>{category}</span>
                      <span className="ml-1 inline-block h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
                    </div>

                    <h3 className="mt-3 text-2xl leading-[1.15] tracking-[-0.018em] text-slate-900 font-default">
                      <span>{titleParts.first}</span>
                      <em className="not-italic" style={{
                    fontStyle: "italic",
                    color: "#2382DA"
                  }}>
                        {titleParts.rest}
                      </em>
                    </h3>

                    {excerpt && <p className="mt-2 line-clamp-1 text-sm leading-[1.55] text-slate-600">
                        {excerpt}
                      </p>}

                    <span className="mt-4 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-blue-900">
                      <span className="border-b-2 border-blue-500 pb-1 transition-all duration-200 group-hover:pb-[2px]">
                        Read More
                      </span>
                      <span className="inline-block h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
                    </span>
                  </div>
                </Link>
              </article>

              {i < rest.length - 1 && <div className="mt-12 flex items-center">
                  <div className="h-px flex-1 bg-slate-300/40" />
                  <span className="h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
                </div>}
            </div>;
      })}
      </div>
    </div>;
}
export default function Expertise() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2063" id="expertise-in-the-field" className="bg-white text-slate-900 py-24 md:py-36 relative overflow-hidden">
      <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-12 gap-x-6 gap-y-10 relative">
          <div className="col-span-12 lg:col-span-4 relative">
            <span aria-hidden className="pointer-events-none absolute -top-8 -left-2 select-none leading-none" style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 800,
            fontSize: "12rem",
            color: "#042E78",
            opacity: 0.05
          }}>
              06
            </span>

            <div className="relative">
              <div className="flex items-center gap-2 font-mono text-[0.78rem] uppercase tracking-[0.18em] text-blue-700">
                <span>§ 06 — Clinical Evidence / Field Reports</span>
              </div>
              <div className="mt-3 flex items-center">
                <div className="h-px w-24 bg-blue-900/40" />
                <span className="h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
              </div>

              <h2 className="font-default mt-8 leading-[1.0] tracking-[-0.028em] text-slate-900" style={{
              fontWeight: 700,
              fontSize: "clamp(2.25rem, 4.6vw, 4.75rem)"
            }}>
                Evidence-based{" "}
                <em className="not-italic" style={{
                fontStyle: "italic",
                color: "#2382DA"
              }}>
                  precision
                </em>
                .
              </h2>

              <Link to="/ServicesPage" className="mt-8 inline-block group">
                <Button variant="link" className="px-0 h-auto font-mono text-[0.78rem] uppercase tracking-[0.18em] text-slate-900 hover:no-underline">
                  <span className="inline-flex items-center gap-2">
                    <span className="border-b-2 border-blue-500 pb-1 transition-all duration-200 group-hover:pb-[2px] group-hover:border-b-[3px]">
                      View All Services
                    </span>
                    <span className="inline-block h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          <span aria-hidden className="hidden lg:block absolute top-0 bottom-0 left-1/3 w-px bg-slate-300/30" />

          <div className="col-span-12 lg:col-span-8 lg:pl-8">
            <p className="text-slate-700 leading-[1.6] font-default" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            maxWidth: "56ch"
          }}>
              With 26 years of certified experience, MYB Services delivers clinical-grade environmental testing. Our field reports provide the empirical data necessary to identify, contain, and resolve mold contamination with absolute certainty.
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-20 flex items-center">
          <div className="h-px flex-1 bg-slate-300/40" />
          <span className="h-2 w-2 rounded-lg bg-blue-500" aria-hidden />
        </div>

        <div className="mt-16 md:mt-20">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date"
        }}>
            <JournalPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}